import { onBeforeUnmount } from 'vue'
import { type RouteLocationNormalizedLoaded, useRoute, useRouter } from 'vue-router'

import { MiloRecordKind } from '@/enums/milo'
import type { IMiloPage } from '@/types/Milo'

/*
 * Where the person asks Milo from (docs/API.md §14.3 MiloMessageRequest `page`): the route's
 * name, and the record open on that page — identifiers only, never content. The drawer stays
 * open across pages, so the page is read from the route at send time. Which pages have a record
 * and where its id sits is this explicit registry, not the DOM: a route parameter, or the query
 * `?open=<id>` the page keeps up to date with `useOpenRecord` while a record is open.
 */

/** The query key a page writes its open record's id under. */
export const OPEN_RECORD_QUERY = 'open'

interface IRecordSource {
    kind:   MiloRecordKind
    /** The route parameter holding the id; without one, `?open=`. */
    param?: string
}

/**
 * Route name → the record its page can have open: the feedback and the request in hand (the
 * detail beside the list), the device and the stay last opened in their drawers (kept after the
 * drawer closes — a click into Milo closes it), a field device by its address. Sobe has no
 * record yet: a room is edited in a modal dialog, and Milo cannot be typed into while it is open.
 */
const RECORD_OF = new Map<string, IRecordSource>([
    ['feedback',     { kind: MiloRecordKind.Feedback }],
    ['requests',     { kind: MiloRecordKind.Request }],
    ['devices',      { kind: MiloRecordKind.Device }],
    ['occupancy',    { kind: MiloRecordKind.Stay }],
    ['field-device', { kind: MiloRecordKind.Device, param: 'id' }],
])

const OBJECT_ID = /^[0-9a-f]{24}$/

/** The contract's route-name pattern; a name outside it is not sent. */
const PAGE_NAME = /^[a-z][a-z0-9-]{0,59}$/

/** A route parameter's or a query's one value. */
const single = (value: unknown): string => {
    if (Array.isArray(value)) return typeof value[0] === 'string' ? value[0] : ''

    return typeof value === 'string' ? value : ''
}

/** The page of a route as Milo is told it; null where there is nothing to tell. */
export const miloPageOf = (route: Pick<RouteLocationNormalizedLoaded, 'name' | 'params' | 'query'>): IMiloPage | null => {
    const name = typeof route.name === 'string' ? route.name : ''

    if (!PAGE_NAME.test(name)) return null

    const source = RECORD_OF.get(name)

    if (!source) return { name, record: null }

    const id = single(source.param ? route.params[source.param] : route.query[OPEN_RECORD_QUERY])

    return { name, record: OBJECT_ID.test(id) ? { kind: source.kind, id } : null }
}

/**
 * For a page with a record in the registry: `set(id)` writes the open record into the address
 * (`?open=`, a replace — no history entry), `set('')` takes it out. Nothing is written once the
 * page is being left, so a closing drawer never touches the next page's address.
 */
export const useOpenRecord = () => {
    const route  = useRoute()
    const router = useRouter()
    const page   = route.name

    let leaving = false

    onBeforeUnmount(() => { leaving = true })

    const set = (id: string) => {
        if (leaving || route.name !== page || single(route.query[OPEN_RECORD_QUERY]) === id) return

        const { [OPEN_RECORD_QUERY]: _old, ...query } = route.query

        void _old
        void router.replace({ query: id ? { ...query, [OPEN_RECORD_QUERY]: id } : query })
    }

    /** The id in the address when the page opened ('' when none), to reopen that record. */
    const initial = single(route.query[OPEN_RECORD_QUERY])

    return { set, initial }
}
